import {Box, Button} from "@mui/material";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {AutoTextSize} from "auto-text-size";

import ResponsiveAppBar from "../assets/components/ResponsiveAppBar.tsx";
import ContactFooter from "./ContactFooter.tsx";
import Footer from "./Footer.tsx";
import React from "react";
import SchoolMap from "../assets/components/SchoolMap.tsx";
import prodigyOwl from "../assets/prodigy-owl-logo.svg";
import microVpkOwl from "../assets/micro-vpk-logo.svg";
import { locationsByState } from "../data/schoolLocations.ts";


export interface LocationStickyProps {
    schoolKey: string,
    name: string,
    address1: string,
    address2: string,
    phone: string,
    email?: string,
    microSchool?: boolean,
    photo?: string,
    grades?: string[],
    tourSchedulingEnabled?: boolean,
    latitude?: number,
    longitude?: number,
    customTourUrl?: string,
}
export const LocationSticky: React.FC<LocationStickyProps> = ({schoolKey, name, address1, address2, phone, microSchool = false, photo}) =>{
    return <Box sx={{
        width: '100%',
        minHeight: 90,
        maxWidth: 500,
        // m: 2,
        // p: 2,
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: 2,
        backgroundColor: 'transparent',
    }}>
        {/* Avatar on the left */}
        <Box sx={{
            width: 90,
            height: 90,
            minWidth: 90,
            minHeight: 90,
            // border: '2px solid black',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            // padding: 1
        }}>
            <img
                src={photo || (microSchool ? microVpkOwl : prodigyOwl)}
                alt="School Logo"
                style={{ width: '100%', height: '100%', objectFit: photo ? 'cover' : 'contain' }}
            />
        </Box>

        {/* Content on the right */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start', minWidth: 0 }}>
            <Box sx={{ width: '100%', mb: 0.5 }}>
                <AutoTextSize
                    maxFontSizePx={20}
                    minFontSizePx={12}
                    style={{
                        fontWeight: 'bold',
                        textAlign: 'left',
                        width: '100%',
                        lineHeight: 1.2
                    }}
                >
                    {name}
                </AutoTextSize>
            </Box>
            <Box sx={{ width: '100%' }}>
                <AutoTextSize
                    maxFontSizePx={14}
                    minFontSizePx={10}
                    style={{
                        textAlign: 'left',
                        width: '100%',
                        color: 'rgba(0, 0, 0, 0.6)',
                        lineHeight: 1.2
                    }}
                >
                    {address1}
                </AutoTextSize>
            </Box>
            <Box sx={{ width: '100%' }}>
                <AutoTextSize
                    maxFontSizePx={14}
                    minFontSizePx={10}
                    style={{
                        textAlign: 'left',
                        width: '100%',
                        color: 'rgba(0, 0, 0, 0.6)',
                        lineHeight: 1.2
                    }}
                >
                    {address2}
                </AutoTextSize>
            </Box>

            {/* Phone number and Contact Us button on the same line */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {phone && (
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <AutoTextSize
                            maxFontSizePx={14}
                            minFontSizePx={10}
                            style={{
                                textAlign: 'left',
                                width: '100%',
                                color: '#00BFFF',
                                lineHeight: 1.2,
                                fontWeight: 'bold'
                            }}
                        >
                            {phone}
                        </AutoTextSize>
                    </Box>
                )}
                <Button
                    variant="contained"
                    href={`/lets-talk?school=${schoolKey}`}
                    sx={{
                        bgcolor: '#00BFFF',
                        color: 'white',
                        textTransform: 'none',
                        borderRadius: 1,
                        px: 0.5,
                        py: 0.25,
                        minWidth: 'auto',
                        fontSize: '0.625rem',
                        fontWeight: 'bold',
                        boxShadow: 'none',
                        '&:hover': {
                            bgcolor: '#0099CC',
                            boxShadow: 'none',
                        }
                    }}
                >
                    Contact
                </Button>
            </Box>
        </Box>
    </Box>;
}

function OurSchools() {
    // Flatten all schools from all states for the map
    const allSchools = Object.values(locationsByState).flat();

    return (
        <>
            <Box sx={{}}>
                <ResponsiveAppBar/>
                <Box className={'tile'} sx={{pt: 5, pb: 5,}}>
                    <SchoolMap schools={allSchools} />
                </Box>
                <Box className={'tile'} sx={{pt: 5, pb: 25,}}>
                    {Object.entries(locationsByState).map(([state, locations]) => (
                        <Accordion key={state} sx={{background: 'transparent', mt: state !== "florida" ? 3 : 0}} elevation={0}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{
                                background: 'transparent', borderRadius: '20px',
                                borderTopLeftRadius: "255px 15px",
                                borderTopRightRadius: "15px 225px",
                                borderBottomRightRadius: "225px 15px",
                                borderBottomLeftRadius: "15px 255px",
                                border: "solid 3px black",
                            }}>
                                <Typography variant={"h4"} fontWeight={"bold"}>{state.toUpperCase()}</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{background: 'transparent'}}>
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(2, 1fr)' },
                                    columnGap: 2,
                                    rowGap: 4,
                                    width: '100%',
                                    justifyItems: 'center'
                                }}>
                                    {locations.map((loc, index) => (
                                        <LocationSticky key={index} {...loc} />
                                    ))}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>

                <ContactFooter/>
                <Footer/>
            </Box>


        </>
    );
}

export default OurSchools
